import dayjs from 'dayjs';

import {
  DateTimeFormat,
  DateTimeHelper,
  FalsyPolicy,
  IsoDateTimeFormat,
} from '@xemplo/date-time';
import { PAYROLL_MODEL_IDS, PayrollModelId, PayrunExtended } from '@xemplo/gp-types';
import { UpdatePayrunMilestonesPayload } from '@xemplo/payrun-query';

import { MILESTONE_FIELDS } from './edit-payrun-milestones.const';
import {
  MilestoneFieldConfig,
  MilestoneFieldValue,
  MilestoneFormValues,
} from './edit-payrun-milestones.types';

/**
 * Time for date-only milestones. Their time picker is read-only and never
 * edited, so it always carries midnight — this keeps a value present so the
 * required check passes while the BE still receives a start-of-day date-time.
 */
export const DATE_ONLY_TIME = '12:00 AM';

const CURRENT_VALUE_FORMAT = 'D MMMM YYYY · h:mm A';
const CURRENT_VALUE_FORMAT_DATE_ONLY = 'D MMMM YYYY';

export function formatCurrentValue(
  milestone: PayrunExtended,
  field: MilestoneFieldConfig,
): string {
  return DateTimeHelper.formatString(milestone[field.source], {
    format: field.dateOnly ? CURRENT_VALUE_FORMAT_DATE_ONLY : CURRENT_VALUE_FORMAT,
    fallback: '—',
    isUtc: false,
  });
}

/**
 * Splits a single milestone value into separate date and time form values.
 * Milestone values are naive Business Unit local times (no `Z`, no offset), so
 * they're read verbatim — no timezone conversion. The date half is a local
 * start-of-day Date — the convention DateFieldRhf emits — so the form value is
 * one type.
 */
function splitDateTime(
  value: Date | string | null | undefined,
  dateOnly = false,
): MilestoneFieldValue {
  if (!value || !DateTimeHelper.isValidDateValue(value)) {
    return { date: null, time: dateOnly ? DATE_ONLY_TIME : '' };
  }
  return {
    date: dayjs(value).startOf('day').toDate(),
    time: dateOnly
      ? DATE_ONLY_TIME
      : DateTimeHelper.formatString(value, { format: DateTimeFormat.HourMinute12 }),
  };
}

/** Splits each milestone date into separate date and time form values */
export function toFormValues(milestone: PayrunExtended): MilestoneFormValues {
  return Object.fromEntries(
    MILESTONE_FIELDS.map((field) => [
      field.key,
      splitDateTime(milestone[field.source], field.dateOnly),
    ]),
  ) as MilestoneFormValues;
}

/**
 * Combines the date and time halves into a single Date holding the picked
 * wall-clock time. The date half is a local start-of-day Date (DateFieldRhf's
 * picked value); the time is applied directly to it. No timezone conversion, so
 * the calendar day and time the user picked are preserved.
 */
export function combineDateTime({ date, time }: MilestoneFieldValue): Date | null {
  if (!date) return null;
  const day = dayjs(date).startOf('day');
  if (!day.isValid()) return null;
  const parsedTime = dayjs(time, DateTimeFormat.HourMinute12);
  if (!parsedTime.isValid()) return day.toDate();
  return day.hour(parsedTime.hour()).minute(parsedTime.minute()).toDate();
}

/**
 * Serialises a milestone to the BE contract (`format: date-time`, ISO-8601).
 * The picked wall-clock time is formatted as-is in Business Unit local time
 * (`isUtc: false`) — no `Z`, no offset shift — so the value round-trips
 * unchanged.
 */
function toPayloadDateTime(value: MilestoneFieldValue) {
  const combined = combineDateTime(value);
  return DateTimeHelper.format(combined, {
    format: IsoDateTimeFormat.IsoDateTime,
    isUtc: false,
    falsyPolicy: FalsyPolicy.Null,
  });
}

/**
 * Combines each field's date and time inputs into the update payload. Outsource
 * pay runs omit `paymentDueDateTime` entirely — the field is hidden, and leaving
 * the key out of the PATCH keeps the existing value untouched.
 */
export function toPayload(
  payrunId: string,
  values: MilestoneFormValues,
  payrollModelId: PayrollModelId,
): UpdatePayrunMilestonesPayload {
  return {
    payrunId,
    amendmentDueDateTime: toPayloadDateTime(values.amendmentDue) ?? null,
    preparationDueDateTime: toPayloadDateTime(values.preparationDue) ?? null,
    approvalDueDateTime: toPayloadDateTime(values.approvalDue) ?? null,
    finaliseDueDateTime: toPayloadDateTime(values.finaliseDue) ?? null,
    ...(payrollModelId !== PAYROLL_MODEL_IDS.OUTSOURCE && {
      paymentDueDateTime: toPayloadDateTime(values.paymentDue) ?? null,
    }),
    payDateTime: toPayloadDateTime(values.payDateTime) ?? null,
    payCycleStartDateTime: toPayloadDateTime(values.payCycleStart) ?? null,
    payCycleEndDateTime: toPayloadDateTime(values.payCycleEnd) ?? null,
  };
}
